/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetClubByIdQuery,
  useGetClubKYCQuery,
} from '@/services/YatchManagementService';
import { useUpdateKYCStatusMutation } from '@/services/ServiceProviderService';
import {
  Container,
  Shimmer,
  KeenIcon,
  SubscriptionPlansCard,
} from '@/components';
import EntityCancelPlanButton from '@/components/subscription-management/EntityCancelPlanButton';
import { Modal } from '@/components/modal';
import { X } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { IClubData } from '@/types/club-management.types';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { ISubscription } from '@/types/subscription-management.types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { setSelectedCommunityId } from '@/store/authSlice';
import { selectClubCapabilities } from '@/store/clubCapabilitiesSlice';
import { KYCStatus } from '@/constant';
import { ClubCapability } from '@/constant/Club';
import { HarborKYCData } from '@/types/harbor-management.types';
import { usePermissions } from '@/hooks/usePermissions';
import { formatInternationalPhoneNumber } from '@/utils';
// import { LegalAcceptanceCard } from '@/components/legal-acceptance/LegalAcceptanceCard';

const CLUB_EDIT_PERMISSION_KEY = 'club_management_update';

const ViewYatchClub = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.auth.userData);
  const { hasPermission } = usePermissions();

  // Get club capabilities from Redux (fetched when clicking club name)
  const clubCapabilities = useSelector(selectClubCapabilities);

  // Check if COMMUNITY_OFFICIAL_GROUPS_BASIC capability is present
  const hasOfficialGroupsBasicCapability = useMemo(() => {
    if (!clubCapabilities || clubCapabilities.length === 0) {
      return false;
    }
    return clubCapabilities.includes(
      ClubCapability.COMMUNITY_OFFICIAL_GROUPS_BASIC
    );
  }, [clubCapabilities]);

  const [, setSelectedClub] = useState<IClubData | null>(null);
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [planCancellationRequested, setPlanCancellationRequested] =
    useState(false);
  const [planSubscriptionEndDate, setPlanSubscriptionEndDate] = useState<
    string | null
  >(null);
  const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);

  // KYC Update Status Modal state
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [kycStatus, setKycStatus] = useState<
    | KYCStatus.PENDING
    | KYCStatus.UNDER_REVIEW
    | KYCStatus.APPROVED
    | KYCStatus.REJECTED
    | KYCStatus.REQUIRES_UPDATE
    | KYCStatus.NOT_REQUIRED
    | KYCStatus.ON_HOLD
  >(KYCStatus.PENDING);
  const [kycNotes, setKycNotes] = useState<string>('');
  const [kycNotesError, setKycNotesError] = useState<string>('');

  // Fetch club details using the ID from the route
  const { data: clubData, isLoading, isError } = useGetClubByIdQuery(id || '');

  const isLoggedInUserClubAdmin =
    !!userData?.id &&
    !!clubData?.yachtClubAdmin?.id &&
    userData.id === clubData.yachtClubAdmin.id;

  const canReadSubscriptions = hasPermission('subscription_management_read');

  const canAccessSubscriptions =
    canReadSubscriptions || isLoggedInUserClubAdmin;

  const canReadKyc = hasPermission('kyc_management_read');

  // Fetch KYC data
  const {
    data: kycDataResponse,
    isLoading: kycLoading,
    error: kycError,
    refetch: refetchKYC,
  } = useGetClubKYCQuery(id || '', {
    skip: !id || !canReadKyc,
  });

  // Handle both wrapped (ApiResponse) and direct response formats
  const kycDataRaw = useMemo(() => {
    if (!kycDataResponse) return null;

    // Check if response has a 'data' property (ApiResponse wrapper)
    // ApiResponse structure: { status: boolean, message?: string, data: T }
    const response = kycDataResponse as unknown as Record<string, unknown>;
    if (
      response?.data &&
      typeof response.data === 'object' &&
      response.data !== null
    ) {
      const data = response.data as Record<string, unknown>;
      // Check if data has KYC fields (id, entity_type, etc.)
      if (data.id && data.entity_type) {
        return data as unknown as HarborKYCData;
      }
    }

    // Check if response has KYC fields directly (id, entity_type, etc.)
    if (response?.id && response?.entity_type) {
      return response as unknown as HarborKYCData;
    }

    // Fallback: return null if structure doesn't match
    console.warn('Unexpected KYC response structure:', kycDataResponse);
    return null;
  }, [kycDataResponse]);

  // API returns plaintext account fields
  const kycData = kycDataRaw;

  // Update KYC Status mutation
  const [updateKYCStatus, { isLoading: isUpdatingKYC }] =
    useUpdateKYCStatusMutation();

  useEffect(() => {
    if (clubData) {
      setSelectedClub(clubData);
    }
  }, [clubData]);

  // Fetch club subscription plans and current plan
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setSubscriptionsLoading(true);

        // Fetch all club subscription plans
        const response = await SubscriptionsService.getSubscriptionsByType(
          'club',
          id ? { clubId: id } : {}
        );
        setSubscriptions(response.subscriptions || []);

        // Fetch current plan for this club
        if (id) {
          const currentPlan = await SubscriptionsService.getCurrentClubPlan(id);
          setCurrentPlanId(currentPlan?.subscription?.id || null);
          setPlanCancellationRequested(
            !!currentPlan?.cancellation_requested_at
          );
          setPlanSubscriptionEndDate(
            currentPlan?.subscription_end_date ?? null
          );
        }
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error);
        setSubscriptions([]);
        setCurrentPlanId(null);
      } finally {
        setSubscriptionsLoading(false);
      }
    };

    if (id && canAccessSubscriptions) {
      fetchSubscriptions();
    } else if (!canAccessSubscriptions) {
      setSubscriptions([]);
      setSubscriptionsLoading(false);
      setCurrentPlanId(null);
    }
  }, [id, canAccessSubscriptions, subscriptionRefreshKey]);

  // Capabilities are now fetched in YatchListing when clicking club name
  // They are stored in Redux and will be available here via selectClubCapabilities
  // No need to fetch capabilities here anymore

  const handleUpgrade = (planId: string) => {
    const selectedPlan = subscriptions.find((s) => s.id === planId);
    if (!selectedPlan || !id) return;

    navigate('/subscription/payment', {
      state: {
        subscriptionId: planId,
        subscriptionName: selectedPlan.name,
        subscriptionPrice: selectedPlan.price,
        entityType: 'club',
        entityId: id,
        returnUrl: `/club-management/${id}`,
      },
    });
  };

  const currentSubscription =
    subscriptions.find((s) => s.id === currentPlanId) || null;

  if (isLoading) {
    return (
      <Container>
        <Shimmer height="200px" />
      </Container>
    );
  }

  if (isError || !clubData) {
    return (
      <Container>
        <div className="p-5 text-red-600" role="alert">
          Error loading data. Please try again.
        </div>
      </Container>
    );
  }

  return (
    <>
      <Container>
        {/* Club Details Section */}
        <div className="card w-full max-w-full mx-auto mb-6">
          <div className="p-5">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-lg font-semibold text-gray-900">
                Club Information
              </h1>
              {hasPermission(CLUB_EDIT_PERMISSION_KEY) && (
                <div className="rounded-full">
                  <button
                    onClick={() =>
                      navigate(`/club-management/edit/${clubData.id}`)
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                    aria-label={`Edit ${clubData.name}`}
                  >
                    <KeenIcon type="filled" icon="pencil" />
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Basic Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-500">
                    Name
                  </span>
                  <span className="mt-1 text-sm text-gray-900">
                    {clubData.name || '-'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-500">
                    Email
                  </span>
                  <span className="mt-1 text-sm text-gray-900 break-all md:break-normal">
                    {clubData.contact_email || '-'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-500">
                    Location
                  </span>
                  <span className="mt-1 text-sm text-gray-900">
                    {clubData.location || '-'}
                  </span>
                </div>
                {clubData.website && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Website
                    </span>
                    <span className="mt-1 text-sm text-blue-600 contrast:text-blue-300 hover:underline">
                      <a
                        href={
                          clubData.website.startsWith('http')
                            ? clubData.website
                            : `https://${clubData.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {clubData.website}
                      </a>
                    </span>
                  </div>
                )}
                {clubData.abbreviation && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Abbreviation
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {clubData.abbreviation}
                    </span>
                  </div>
                )}
                {clubData.contact_person_name && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Contact Person Name
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {clubData.contact_person_name}
                    </span>
                  </div>
                )}
                {clubData.contact_person_phone && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Contact Person Phone
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {formatInternationalPhoneNumber(
                        clubData.contact_person_phone,
                        clubData.country_code,
                        clubData.country_short_code
                      )}
                    </span>
                  </div>
                )}
                {clubData.street_address && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Street Address
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {clubData.street_address}
                    </span>
                  </div>
                )}
                {clubData.postal_code && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Postal Code
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {clubData.postal_code}
                    </span>
                  </div>
                )}
                {clubData.city && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      City
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {clubData.city}
                    </span>
                  </div>
                )}
                {clubData.business_id && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Business ID
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {clubData.business_id}
                    </span>
                  </div>
                )}
                {clubData.spv_id && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      SPV ID
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {clubData.spv_id}
                    </span>
                  </div>
                )}
                {clubData.region_id && (
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500">
                      Region
                    </span>
                    <span className="mt-1 text-sm text-gray-900">
                      {clubData.region?.name}
                    </span>
                  </div>
                )}
                {hasOfficialGroupsBasicCapability &&
                  hasPermission('community_management_read') && (
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-500">
                        Club Forum
                      </span>
                      {clubData.community ? (
                        <button
                          onClick={() => {
                            dispatch(
                              setSelectedCommunityId({
                                selectedCommunityId:
                                  clubData.community?.id || '',
                              })
                            );
                            navigate('/community/details');
                          }}
                          className="mt-1 text-sm text-blue-600 contrast:text-blue-300 hover:underline text-left"
                        >
                          {clubData.community.name}
                        </button>
                      ) : (
                        <span className="mt-1 text-sm text-gray-900">-</span>
                      )}
                    </div>
                  )}
              </div>

              {/* Description */}
              {clubData.description && (
                <div className="mt-4">
                  <h2 className="text-sm font-medium text-gray-500 mb-1">
                    Description
                  </h2>
                  <p className="text-sm text-gray-900 whitespace-pre-line">
                    {clubData.description}
                  </p>
                </div>
              )}

              {/* Admin Details */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h2 className="text-sm font-medium text-gray-900 mb-3">
                  Admin Details
                </h2>
                {clubData.yachtClubAdmin ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-500">
                        Admin Name
                      </span>
                      <span className="mt-1 text-sm text-gray-900">
                        {clubData.yachtClubAdmin.name || '-'}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-500">
                        Admin Email
                      </span>
                      <a
                        href={`mailto:${clubData.yachtClubAdmin.email}`}
                        className="mt-1 text-sm text-blue-600 contrast:text-blue-300 hover:underline break-all md:break-normal"
                      >
                        {clubData.yachtClubAdmin.email || '-'}
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No admin assigned</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* <LegalAcceptanceCard summary={clubData?.legal_acceptance_summary} /> */}

        {/* KYC Details Card */}
        {canReadKyc && (
          <div className="card w-full max-w-full mx-auto mb-6">
            <div className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  KYC Details
                </h3>
                {hasPermission('kyc_management_change_status') && (
                  <button
                    type="button"
                    onClick={() => {
                      setKycStatus(
                        (kycData?.kyc_status as
                          | KYCStatus.PENDING
                          | KYCStatus.UNDER_REVIEW
                          | KYCStatus.APPROVED
                          | KYCStatus.REJECTED
                          | KYCStatus.REQUIRES_UPDATE
                          | KYCStatus.NOT_REQUIRED
                          | KYCStatus.ON_HOLD) || KYCStatus.PENDING
                      );
                      setKycNotes(kycData?.admin_notes || '');
                      setKycNotesError('');
                      setIsUpdateStatusModalOpen(true);
                    }}
                    disabled={!kycData || kycLoading}
                    className="btn btn-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Update Status
                  </button>
                )}
              </div>

              {kycLoading ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-gray-500">Loading KYC data...</p>
                </div>
              ) : kycError ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-red-500">
                    Failed to load KYC data. Please try again later.
                  </p>
                </div>
              ) : !kycData ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-gray-500">
                    No KYC data available.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {kycData.legal_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Legal Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.legal_name}
                        </span>
                      </div>
                    )}
                    {kycData.business_id && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Business ID
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.business_id}
                        </span>
                      </div>
                    )}
                    {kycData.vat_id && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          VAT ID
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.vat_id}
                        </span>
                      </div>
                    )}
                    {kycData.address_line_1 && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Address Line 1
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.address_line_1}
                        </span>
                      </div>
                    )}
                    {kycData.address_line_2 && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Address Line 2
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.address_line_2}
                        </span>
                      </div>
                    )}
                    {kycData.postal_code && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Postal Code
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.postal_code}
                        </span>
                      </div>
                    )}
                    {kycData.city && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          City
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.city}
                        </span>
                      </div>
                    )}
                    {kycData.state && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          State
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.state}
                        </span>
                      </div>
                    )}
                    {kycData.country && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Country
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.country}
                        </span>
                      </div>
                    )}
                    {kycData.account_holder_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Account Holder Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all whitespace-normal overflow-hidden">
                          {kycData.account_holder_name}
                        </span>
                      </div>
                    )}
                    {kycData.account_number && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Account Number
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all whitespace-normal overflow-hidden">
                          {kycData.account_number}
                        </span>
                      </div>
                    )}
                    {kycData.iban && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          IBAN
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono break-all md:break-normal">
                          {kycData.iban}
                        </span>
                      </div>
                    )}
                    {kycData.bic && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          BIC
                        </span>
                        <span className="mt-1 text-sm text-gray-900 font-mono">
                          {kycData.bic}
                        </span>
                      </div>
                    )}
                    {kycData.bank_name && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          Bank Name
                        </span>
                        <span className="mt-1 text-sm text-gray-900">
                          {kycData.bank_name}
                        </span>
                      </div>
                    )}
                    {kycData.kyc_status && (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          KYC Status
                        </span>
                        <span className="mt-1">
                          <span
                            className={`text-sm font-semibold capitalize ${
                              (kycData.kyc_status as string) ===
                              KYCStatus.APPROVED
                                ? 'text-green-600'
                                : (kycData.kyc_status as string) ===
                                    KYCStatus.REJECTED
                                  ? 'text-red-600'
                                  : (kycData.kyc_status as string) ===
                                        KYCStatus.UNDER_REVIEW ||
                                      (kycData.kyc_status as string) ===
                                        KYCStatus.REQUIRES_UPDATE ||
                                      (kycData.kyc_status as string) ===
                                        KYCStatus.NOT_REQUIRED ||
                                      (kycData.kyc_status as string) ===
                                        KYCStatus.ON_HOLD
                                    ? 'text-yellow-600'
                                    : 'text-gray-600'
                            }`}
                          >
                            {(kycData.kyc_status as string) ===
                            KYCStatus.PENDING
                              ? 'Pending'
                              : (kycData.kyc_status as string) ===
                                  KYCStatus.UNDER_REVIEW
                                ? 'Under Review'
                                : (kycData.kyc_status as string) ===
                                    KYCStatus.APPROVED
                                  ? 'Approved'
                                  : (kycData.kyc_status as string) ===
                                      KYCStatus.REJECTED
                                    ? 'Rejected'
                                    : (kycData.kyc_status as string) ===
                                        KYCStatus.REQUIRES_UPDATE
                                      ? 'Requires Update'
                                      : (kycData.kyc_status as string) ===
                                          KYCStatus.NOT_REQUIRED
                                        ? 'Not Required'
                                        : (kycData.kyc_status as string) ===
                                            KYCStatus.ON_HOLD
                                          ? 'On Hold'
                                          : (kycData.kyc_status as string) ===
                                              KYCStatus.RESUBMITTED
                                            ? 'Resubmitted'
                                            : kycData.kyc_status}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Admin Notes */}
                  {kycData.admin_notes && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-1">
                        Admin Notes
                      </h4>
                      <p className="text-sm text-gray-900 whitespace-pre-line">
                        {kycData.admin_notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* KYC Update Status Modal */}
        <Modal
          open={isUpdateStatusModalOpen}
          onClose={() => {
            setIsUpdateStatusModalOpen(false);
            setKycNotesError('');
          }}
          className="!flex items-center justify-center"
        >
          <div className="bg-gray-100 p-6 w-full max-w-md mx-auto rounded-lg shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Update KYC Status
              </h2>
              <button
                type="button"
                onClick={() => {
                  setIsUpdateStatusModalOpen(false);
                  setKycNotesError('');
                }}
                aria-label="Close update KYC status dialog"
                title="Close"
                className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 contrast:text-white"
              >
                <X className="h-5 w-5" aria-hidden="true" focusable="false" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="kyc-status"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Status
                </label>
                <select
                  id="kyc-status"
                  value={kycStatus}
                  onChange={(e) =>
                    setKycStatus(
                      e.target.value as
                        | KYCStatus.PENDING
                        | KYCStatus.UNDER_REVIEW
                        | KYCStatus.APPROVED
                        | KYCStatus.REJECTED
                        | KYCStatus.REQUIRES_UPDATE
                        | KYCStatus.NOT_REQUIRED
                        | KYCStatus.ON_HOLD
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-200 text-gray-900"
                >
                  <option value={KYCStatus.PENDING}>Pending</option>
                  <option value={KYCStatus.UNDER_REVIEW}>Under Review</option>
                  <option value={KYCStatus.APPROVED}>Approved</option>
                  <option value={KYCStatus.REJECTED}>Rejected</option>
                  <option value={KYCStatus.NOT_REQUIRED}>Not Required</option>
                  <option value={KYCStatus.REQUIRES_UPDATE}>
                    Requires Update
                  </option>
                  <option value={KYCStatus.ON_HOLD}>On Hold</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="kyc-notes"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Notes <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="kyc-notes"
                  value={kycNotes}
                  onChange={(e) => {
                    setKycNotes(e.target.value);
                    if (e.target.value.trim()) {
                      setKycNotesError('');
                    }
                  }}
                  rows={4}
                  className={`textarea w-full px-3 py-2 rounded-md resize-none ${
                    kycNotesError
                      ? 'border border-red-500 focus:ring-red-500'
                      : 'bg-gray-200'
                  }`}
                  placeholder="Enter admin notes..."
                />
                {kycNotesError && (
                  <p className="mt-1 text-xs text-red-500">{kycNotesError}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsUpdateStatusModalOpen(false);
                  setKycNotesError('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!kycData?.id) {
                    console.error('KYC ID not available');
                    return;
                  }

                  if (!kycNotes.trim()) {
                    setKycNotesError('Notes are required');
                    return;
                  }

                  try {
                    await updateKYCStatus({
                      kycId: kycData.id,
                      entityId: id || '',
                      data: {
                        kyc_status: kycStatus,
                        admin_notes: kycNotes,
                      },
                    }).unwrap();

                    // Refetch KYC data after successful update
                    await refetchKYC();

                    setIsUpdateStatusModalOpen(false);
                    // You can add a success notification here
                  } catch (error) {
                    console.error('Failed to update KYC status:', error);
                    // You can add an error notification here
                  }
                }}
                disabled={isUpdatingKYC || !kycData?.id}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingKYC ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Associated Harbor Details Section */}
        <div className="card w-full max-w-full mx-auto mb-6">
          <div className="p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Associated Harbor Details
            </h3>
            {clubData?.harbors && clubData?.harbors?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clubData?.harbors?.map(
                  (harbor: { id: string; name: string; location: string }) => (
                    <div key={harbor.id} className="border rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">
                        {harbor.name}
                      </h4>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600">
                          {harbor.location}
                        </p>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                No associated harbors found
              </p>
            )}
          </div>
        </div>

        {/* Media Section */}
        <div className="card w-full max-w-full mx-auto mb-6">
          <div className="p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Media</h3>
            {clubData?.media?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-8 gap-1">
                {clubData?.media?.map((media: any, index: number) => {
                  // Infer media type from URL extension
                  const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(media);
                  const isVideo = /\.(mp4|webm|ogg)$/i.test(media);

                  return (
                    <div
                      key={index}
                      className="relative w-[100px] h-[100px] rounded-lg overflow-hidden border"
                    >
                      {isImage ? (
                        <img
                          src={media}
                          alt={`Media ${index + 1}`}
                          className="w-[100px] h-[100px] object-cover"
                        />
                      ) : isVideo ? (
                        <video
                          src={media}
                          className="w-[100px] h-[100px] object-cover"
                          controls
                        />
                      ) : (
                        <div className="w-[100px] h-[100px] flex items-center justify-center bg-gray-100">
                          <span className="text-gray-500">
                            Unsupported media type
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <p className="text-gray-500">No media available</p>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Plans Section */}
        {canAccessSubscriptions && !isLoggedInUserClubAdmin && (
          <div className="card w-full max-w-full mx-auto">
            <div className="p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Club Subscription Plans
              </h3>
              {id && (
                <EntityCancelPlanButton
                  entityType="club"
                  entityId={id}
                  entityName={clubData?.name}
                  planPrice={currentSubscription?.price}
                  cancellationRequested={planCancellationRequested}
                  subscriptionEndDate={planSubscriptionEndDate}
                  onSuccess={() =>
                    setSubscriptionRefreshKey((key) => key + 1)
                  }
                  className="mb-4"
                />
              )}
              {subscriptionsLoading ? (
                <SubscriptionPlansCard
                  subscriptions={
                    currentSubscription ? [currentSubscription] : []
                  }
                  loading={subscriptionsLoading}
                  currentPlanId={currentPlanId}
                />
              ) : currentSubscription ? (
                <SubscriptionPlansCard
                  subscriptions={[currentSubscription]}
                  loading={false}
                  currentPlanId={currentPlanId}
                />
              ) : (
                <p className="text-sm text-gray-500">
                  No current plan found for this club.
                </p>
              )}
            </div>
          </div>
        )}
        {canAccessSubscriptions && isLoggedInUserClubAdmin && (
          <div className="card w-full max-w-full mx-auto">
            <div className="p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Club Subscription Plans
              </h3>
              <SubscriptionPlansCard
                subscriptions={subscriptions}
                loading={subscriptionsLoading}
                currentPlanId={currentPlanId}
                onUpgrade={handleUpgrade}
              />
            </div>
          </div>
        )}
      </Container>
    </>
  );
};

export default ViewYatchClub;
